import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Truck, Package, Layers, AlertCircle, Factory, ShoppingCart, Image, AlertTriangle, ExternalLink, CheckCircle } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

export default function SOLineItemDetailView({
  order,
  lineItem: li,
  workOrders,
  canSeePrices,
  canEditSO,
  onBack,
  onDispatch,
  onEditVariants,
  onDeleteLineItem,
  hasItemBOM,
}) {
  const navigate = useNavigate();
  if (!order || !li) return null;

  const hasBOM = hasItemBOM(li.item_id);
  const existingWO = workOrders.find(wo => wo.sales_order_id === order.id && (wo.line_item_id === li.id || wo.item_id === li.item_id));
  const woAllocs = li.wo_allocations || [];
  const dispatched = li.dispatched_quantity || 0;
  const remaining = li.quantity - dispatched;
  const dispatchPct = li.quantity > 0 ? Math.round((dispatched / li.quantity) * 100) : 0;
  const fulfillment = li.fulfillment_type || (li.item_source_type === 'IP' ? 'Make' : 'Buy');
  const canDispatch = remaining > 0 && order.status !== 'cancelled';

  // Dispatch history
  const dispatches = li.dispatch_history || [];

  // Specs
  const specs = typeof li.specs === 'string' ? li.specs : typeof li.specs === 'object' ? Object.entries(li.specs || {}).filter(([_, v]) => v).map(([k, v]) => `${k}: ${v}`).join('\n') : '';

  return (
    <div className="space-y-5">
      {/* Back + Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="mt-0.5 shrink-0" data-testid="li-detail-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to {order.so_number}
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            {/* SO Line Item Number - Primary identifier */}
            {li.item_code && (
              <span className="text-lg font-mono font-semibold text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded border border-blue-200" data-testid="li-item-code">
                {li.item_code}
              </span>
            )}
            <h2 className="text-xl font-semibold tracking-tight">{li.item_name}</h2>
            {hasBOM && <Badge variant="outline" className="text-xs bg-green-50 text-green-700">BOM</Badge>}
            <Badge variant="outline" className={`text-xs ${fulfillment === 'Make' ? 'bg-orange-50 text-orange-700' : 'bg-purple-50 text-purple-700'}`}>
              {fulfillment === 'Make' ? <Factory className="w-3 h-3 mr-0.5" /> : <ShoppingCart className="w-3 h-3 mr-0.5" />}
              {fulfillment}
            </Badge>
            {li.is_fulfillment_override && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700"><AlertTriangle className="w-3 h-3 mr-0.5" />Override</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {order.so_number} &middot; {order.customer_name}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {li.needs_variant_selection && canEditSO && (
            <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50"
              onClick={onEditVariants}>
              <Layers className="w-3.5 h-3.5 mr-1" /> Select Variants
            </Button>
          )}
        </div>
      </div>

      {/* Item image + details */}
      <div className="flex gap-6">
        {li.item_image ? (
          <img src={li.item_image} alt={li.item_name} className="w-24 h-24 object-cover rounded-lg border shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-24 h-24 bg-muted rounded-lg border flex items-center justify-center shrink-0">
            <Image className="w-8 h-8 text-muted-foreground/40" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {li.variant_code && li.variant_code !== li.item_name?.toUpperCase() && (
            <p className="text-sm font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded inline-block mb-2">{li.variant_code}</p>
          )}
          {specs && (
            <div className="text-sm text-muted-foreground bg-muted/40 rounded-lg px-4 py-2 mb-2 whitespace-pre-line">
              <span className="font-medium text-foreground">Specs:</span> {specs}
            </div>
          )}
          {li.remarks && (
            <div className="text-sm text-orange-700 bg-orange-50 rounded-lg px-4 py-2 whitespace-pre-line">
              <span className="font-medium">Remarks:</span> {typeof li.remarks === 'object' ? '' : li.remarks}
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {li.has_bom === false && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-orange-500" />
          <span className="text-sm text-orange-800 font-medium">Pending BOM</span>
          <span className="text-sm text-orange-600">— Waiting for Product Designer to create Bill of Materials</span>
        </div>
      )}
      {li.needs_variant_selection && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <Layers className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-amber-800 font-medium">Needs Variant Selection</span>
          <span className="text-sm text-amber-600">— Select specific material variants before WO can proceed</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Ordered" value={`${li.quantity} pcs`} />
        {canSeePrices && <SummaryCard label="Unit Price" value={`₹${(li.unit_price || 0).toLocaleString()}`} />}
        {canSeePrices && <SummaryCard label="Amount" value={`₹${(li.amount || 0).toLocaleString()}`} />}
        <SummaryCard label="Dispatched" value={`${dispatched} / ${li.quantity}`}>
          <Progress value={dispatchPct} className="h-1.5 mt-1" />
        </SummaryCard>
        <SummaryCard label="Remaining" value={`${remaining} pcs`} highlight={remaining > 0} />
      </div>

      {/* Fulfillment details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* WO Allocation */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Factory className="w-4 h-4" /> Work Order Allocation
            </h3>
            {woAllocs.length > 0 ? (
              <div className="space-y-2">
                {woAllocs.map((alloc, ai) => (
                  <div key={ai} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                    <div>
                      <Button variant="link" size="sm" className="h-auto p-0 text-blue-600 font-medium"
                        onClick={() => navigate(`/production/work-orders/view/${alloc.work_order_id}`)}>
                        {alloc.work_order_number} <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                      <p className="text-xs text-muted-foreground">WO: {alloc.quantity} pcs</p>
                    </div>
                    {(alloc.stock_qty > 0 || li.stock_fulfill_qty > 0) && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                        Stock: {alloc.stock_qty || li.stock_fulfill_qty} pcs
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : existingWO ? (
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                <Button variant="link" size="sm" className="h-auto p-0 text-blue-600 font-medium"
                  onClick={() => navigate(`/production/work-orders/view/${existingWO.id}`)}>
                  {existingWO.work_order_number} <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
                <span className="text-xs text-muted-foreground">{existingWO.quantity} pcs</span>
              </div>
            ) : fulfillment === 'Make' ? (
              <p className="text-sm text-orange-600 py-2">No Work Order created yet</p>
            ) : (
              <p className="text-sm text-muted-foreground py-2">Buy item — no WO needed</p>
            )}

            {/* Stock fulfillment */}
            {!woAllocs.length && li.stock_fulfill_qty > 0 && (
              <div className="mt-2 p-2 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700 font-medium">Stock: {li.stock_fulfill_qty} pcs from inventory</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Variant Selections */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-purple-600" /> Variant Selections
            </h3>
            {li.variant_selections?.length > 0 ? (
              <div className="space-y-2">
                {li.variant_selections.map((vs, idx) => (
                  <div key={idx} className={`p-2 rounded-lg ${vs.selection_pending ? 'bg-amber-50 border border-amber-200' : 'bg-purple-50/50'}`}>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{vs.variant_group_name}</p>
                    <p className={`text-sm font-medium mt-0.5 ${vs.selection_pending ? 'text-amber-700' : 'text-purple-700'}`}>
                      {vs.selection_pending ? 'Pending — Decide Later' : vs.selected_item_name}
                    </p>
                    <p className="text-xs text-muted-foreground">{vs.quantity_per_unit} {vs.unit}/unit = {(vs.quantity_per_unit * li.quantity).toFixed(1)} {vs.unit} total</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-2">No variant materials for this item</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dispatch History */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Truck className="w-4 h-4 text-teal-600" /> Dispatch History
            </h3>
            <p className="text-xs text-muted-foreground">Dispatch via SO Fulfillment page</p>
          </div>
          {dispatches.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead>Date</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>LR No.</TableHead>
                  <TableHead>Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dispatches.map((d, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-sm">{d.dispatch_date ? formatDate(d.dispatch_date) : '-'}</TableCell>
                    <TableCell className="text-center font-medium">{d.quantity_dispatched}</TableCell>
                    <TableCell className="text-sm font-mono">{d.invoice_number || '-'}</TableCell>
                    <TableCell className="text-sm font-mono">{d.lr_number || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{d.remarks || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : dispatched > 0 ? (
            <div className="p-3 bg-muted/30 rounded-lg text-center">
              <p className="text-sm text-muted-foreground">{dispatched} unit(s) dispatched (detailed history not available for older dispatches)</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">No dispatches recorded yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, children, highlight }) {
  return (
    <div className={`rounded-lg px-4 py-3 ${highlight ? 'bg-orange-50 border border-orange-200' : 'bg-muted/30'}`}>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-semibold mt-0.5 ${highlight ? 'text-orange-700' : ''}`}>{value}</p>
      {children}
    </div>
  );
}
